import { FormattedEvent } from '@eventbrite/event-renderer';
import { format, nextMonday, parseISO } from 'date-fns';
import { EVENTBRITE_FORMAT } from '../../constants';

/**
 * Checks if the event date is on the next weekend.
 * @param eventDate - The date of the event.
 * @param startDate - The start date to compare with.
 * @param includeFriday - Whether to include Friday as part of the weekend. Default is true.
 * @returns True if the event date is on the next weekend, false otherwise.
 */
export const isNextWeekend = (
    eventDate: string,
    startDate: string,
    includeFriday = true,
): boolean => {
    const weekDays = [
        'Saturday',
        'Sunday',
        ...(includeFriday ? ['Friday'] : []),
    ];

    // Create a date object from the date string
    const eventDateObj = parseISO(`${eventDate}T00:00:00`);
    const startDateObj = parseISO(`${startDate}T00:00:00`);

    // Get the day of the week of the date object (e.g. 'Monday')
    const dayOfWeek = format(eventDateObj, 'EEEE');

    const isWeekendDay = weekDays.includes(dayOfWeek);
    const isBeforeNextMonday = eventDateObj < nextMonday(startDateObj);
    const isAfterStartDate = eventDateObj >= startDateObj;

    return isBeforeNextMonday && isWeekendDay && isAfterStartDate;
};

/**
 * Gets the event category format from the event tags.
 * @param event - The event object transformed.
 * @returns The event category details.
 */
export const getEventCategoryFormat = (event: FormattedEvent) => {
    const eventCategory = event.tags?.find((tag) =>
        tag.includes(EVENTBRITE_FORMAT),
    );
    return eventCategory;
};
